import React from 'react';
import { motion } from 'framer-motion';
import { format, addDays, startOfWeek, addWeeks, addMonths } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiClock, FiMapPin, FiArrowRight, FiUsers, FiHeart, FiMusic, FiWifi } = FiIcons;

const Events = () => {
  const today = new Date();
  const weekStart = startOfWeek(today);

  const upcomingEvents = [
    {
      title: 'Marriage University',
      date: addDays(today, 7),
      time: '4:00 PM',
      location: 'Main Sanctuary',
      description: 'Building stronger marriages through biblical principles and practical wisdom.',
      type: 'Marriage',
      color: 'pink',
      icon: FiHeart
    },
    {
      title: 'Every Hour Prayer Community',
      date: addDays(today, 2),
      time: 'Every Hour',
      location: 'Prayer Room',
      description: 'Join our continuous prayer chain that covers every hour of the day.',
      type: 'Prayer',
      color: 'purple',
      icon: FiUsers
    },
    {
      title: 'Saviours Youth Church',
      date: addDays(today, 5),
      time: '6:00 PM',
      location: 'Youth Center',
      description: 'Dynamic youth service with contemporary worship and relevant teaching.',
      type: 'Youth',
      color: 'green',
      icon: FiUsers
    },
    {
      title: 'Dunamis 7000 Teens',
      date: addDays(today, 6),
      time: '5:00 PM',
      location: 'Teen Hall',
      description: 'Empowering teenagers with God\'s power for this generation.',
      type: 'Teens',
      color: 'blue',
      icon: FiUsers
    },
    {
      title: 'Men\'s Vigil',
      date: addDays(today, 26), // Last Friday of month
      time: '11:00 PM',
      location: 'Main Sanctuary',
      description: 'Monthly men\'s fellowship vigil held every last Friday of the month.',
      type: 'Men\'s Fellowship',
      color: 'red',
      icon: FiUsers
    },
    {
      title: 'Women\'s Meeting',
      date: addDays(today, 15), // Third Tuesday
      time: '6:00 PM',
      location: 'Fellowship Hall',
      description: 'Women\'s fellowship meeting held every third Tuesday of the month.',
      type: 'Women\'s Fellowship',
      color: 'yellow',
      icon: FiHeart
    },
    {
      title: 'Abide Musical Concert (4th Edition)',
      date: addDays(today, 21),
      time: '6:00 PM',
      location: 'Main Sanctuary',
      description: 'An evening of worship and praise featuring local and guest artists.',
      type: 'Concert',
      color: 'indigo',
      icon: FiMusic
    },
    {
      title: 'Digital Detox & Structure Network',
      date: addDays(today, 14),
      time: '10:00 AM',
      location: 'Conference Room',
      description: 'Learn to balance technology use and build meaningful connections.',
      type: 'Workshop',
      color: 'gray',
      icon: FiWifi
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-700',
      green: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700',
      purple: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 border-purple-200 dark:border-purple-700',
      yellow: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-700',
      red: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-200 dark:border-red-700',
      pink: 'bg-pink-100 dark:bg-pink-900 text-pink-800 dark:text-pink-200 border-pink-200 dark:border-pink-700',
      indigo: 'bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 border-indigo-200 dark:border-indigo-700',
      gray: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-600'
    };
    return colors[color] || colors.blue;
  };

  return (
    <section id="events" className="py-20 bg-white dark:bg-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Upcoming
            <span className="block text-blue-600 dark:text-blue-400">Events & Activities</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Stay connected with our church community through our various events, 
            services, and special programs throughout the year.
          </p>
        </motion.div>

        {/* Events Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {upcomingEvents.map((event, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 hover:shadow-lg transition-all duration-300 group cursor-pointer"
            >
              {/* Event Type Badge & Icon */}
              <div className="flex justify-between items-start mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getColorClasses(event.color)}`}>
                  {event.type}
                </span>
                <SafeIcon icon={event.icon} className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              </div>

              {/* Event Title */}
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                {event.title}
              </h3>

              {/* Event Details */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-gray-600 dark:text-gray-300">
                  <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2" />
                  <span className="text-sm">{format(event.date, 'EEEE, MMMM do')}</span>
                </div>
                <div className="flex items-center text-gray-600 dark:text-gray-300">
                  <SafeIcon icon={FiClock} className="w-4 h-4 mr-2" />
                  <span className="text-sm">{event.time}</span>
                </div>
                <div className="flex items-center text-gray-600 dark:text-gray-300">
                  <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-2" />
                  <span className="text-sm">{event.location}</span>
                </div>
              </div>

              {/* Event Description */}
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{event.description}</p>

              {/* Learn More Button */}
              <button className="flex items-center text-blue-600 dark:text-blue-400 font-semibold text-sm group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors duration-300">
                Learn More
                <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Newsletter Signup */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 rounded-xl p-8 text-white text-center"
        >
          <h3 className="text-3xl font-bold mb-4">Stay Updated</h3>
          <p className="text-blue-100 dark:text-blue-50 mb-6 max-w-2xl mx-auto">
            Never miss an event! Subscribe to our newsletter to receive updates about 
            upcoming services, special events, and church announcements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg text-gray-900 dark:text-gray-100 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
            <button className="bg-yellow-400 hover:bg-yellow-500 dark:bg-yellow-300 dark:hover:bg-yellow-400 text-black font-semibold px-6 py-3 rounded-lg transition-colors duration-300">
              Subscribe
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Events;